import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useMfa } from '@/hooks/useMfa';
import { useSingleSessionStatus } from '@/hooks/useSingleSession';
import { MFA_BYPASS, MFA_REQUIRED, MFA_ROUTE, isMfaDeviceRemembered, needsChallenge, needsEnrollment } from '@/lib/mfa';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipOnboardingCheck?: boolean;
  skipLicenseCheck?: boolean;
}

export const ProtectedRoute = ({ children, skipOnboardingCheck = false, skipLicenseCheck = false }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { currentLevel, nextLevel, loading: mfaLoading } = useMfa();
  const sessionStatus = useSingleSessionStatus();
  const location = useLocation();

  // Wait for auth to finish loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Single-session claim still in flight. Holding here keeps a device that is
  // about to be refused from flashing the dashboard on its way back to /auth.
  if (sessionStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Two-factor gate. This runs before every other check — a half-authenticated
  // session must not reach onboarding, the dashboard, or anything else. It lives
  // here rather than in Auth.tsx because signInWithPassword returns a perfectly
  // valid *aal1* session, so the redirect in Auth.tsx fires regardless; gating at
  // the route boundary also covers Google OAuth and restored sessions.
  // Sandbox demo accounts skip mandatory enrollment — read from user metadata so
  // it resolves before the profile fetch. Regular accounts are unaffected.
  const isDemoAccount = user.user_metadata?.is_demo === true;

  if (!MFA_BYPASS && location.pathname !== MFA_ROUTE) {
    if (mfaLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    // Verified factor exists but this token hasn't answered a challenge yet.
    // A browser that answered a challenge in the last MFA_REMEMBER_HOURS is
    // trusted, so skip the prompt — the session stays aal1, the user is just not
    // asked for a code. Enrollment (below) is never skipped this way.
    if (needsChallenge(currentLevel, nextLevel) && !isMfaDeviceRemembered(user.id)) {
      return <Navigate to={MFA_ROUTE} state={{ from: location }} replace />;
    }

    // No factor enrolled at all, and enrollment is mandatory.
    if (MFA_REQUIRED && !isDemoAccount && needsEnrollment(nextLevel)) {
      return <Navigate to={MFA_ROUTE} state={{ from: location }} replace />;
    }
  }


  if (skipOnboardingCheck) {
    return <div data-app-shell className="contents">{children}</div>;
  }

  // Wait for the initial profile fetch to complete before rendering children.
  // Without this, a freshly-signed-in user can land on /dashboard with profile=null,
  // skipping the onboarding redirect and showing a blank screen.
  if (profileLoading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isStudentExperience = profile?.role === 'student';

  // Phase 1 onboarding (goals/preferences): students only (includes "sales professionals" who sign up without an invite)
  if (
    profile &&
    isStudentExperience &&
    !profile.onboarding_completed_at &&
    location.pathname !== '/onboarding'
  ) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] Redirecting to /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Phase 2 profile completion: students only (NOT sales professionals)
  if (
    profile &&
    isStudentExperience &&
    profile.user_type !== 'professional' &&
    profile.onboarding_completed_at &&
    !profile.profile_completed_at &&
    location.pathname !== '/profile-setup'
  ) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] Redirecting to /profile-setup');
    return <Navigate to="/profile-setup" replace />;
  }

  return <div data-app-shell className="contents">{children}</div>;
};
